import { CalendarSettingsViewModel } from "../api/calendar-settings"
import { CalendarSettingsHoliday } from "../api/calendar-settings-response"

type HolidayChecker = (date: Date | string) => boolean
type WeekendChecker = (date: Date | string) => boolean

type DateKeys = {
  dateKey: string
  monthDayKey: string
}

type DateParts = {
  year: string
  month: string
  day: string
}

const DATE_KEY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/ // matches "YYYY-MM-DD" format
const DAY_OF_WEEK_MAP: Record<string, DayOfWeek> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

export const createHolidayChecker = (
  calendarSettings: Pick<CalendarSettingsViewModel, "recurringHolidays" | "fixedHolidays" | "timeZone">
): HolidayChecker => {
  const fixedHolidayDateKeys = new Set(calendarSettings.fixedHolidays)
  const recurringHolidayMonthDayKeys = new Set(calendarSettings.recurringHolidays)

  return (date: Date | string): boolean => {
    const keys = buildDateKeysForDate(date, calendarSettings.timeZone)
    if (!keys) return false

    const { dateKey, monthDayKey } = keys
    return fixedHolidayDateKeys.has(dateKey) || recurringHolidayMonthDayKeys.has(monthDayKey)
  }
}

export const createWeekendChecker = (
  calendarSettings: Pick<CalendarSettingsViewModel, "weekendDays" | "timeZone">
): WeekendChecker => {
  const weekendDays = new Set(calendarSettings.weekendDays)

  return (date: Date | string): boolean => {
    if (weekendDays.size === 0) return false
    const dayOfWeek = extractDayOfWeek(date, calendarSettings.timeZone)
    if (dayOfWeek === null) return false

    return weekendDays.has(dayOfWeek)
  }
}

export const findHolidayDetailsForDate = (
  date: Date,
  calendarSettings: CalendarSettingsViewModel
): CalendarSettingsHoliday | null => {
  const keys = buildDateKeysForDate(date, calendarSettings.timeZone)
  if (!keys) return null

  const { dateKey, monthDayKey } = keys
  const found = calendarSettings.holidayDetails.find((holiday) => {
    const isMatchingFixedHoliday = holiday.dateKey === dateKey
    const isMatchingRecurringHoliday = holiday.dateKey === monthDayKey
    return isMatchingFixedHoliday || isMatchingRecurringHoliday
  })

  return found || null
}

export const getHolidayNameForDate = (
  date: Date,
  calendarSettings: CalendarSettingsViewModel,
  defaultName = "Holiday"
): string => {
  const holidayDetails = findHolidayDetailsForDate(date, calendarSettings)
  return holidayDetails?.name || defaultName
}

const buildDateKeysForDate = (date: Date | string, timeZone: string): DateKeys | null => {
  if (typeof date === "string") {
    // First try to parse the string as a date key (YYYY-MM-DD)
    const mayBeDateKey = extractDatePartsFromDateKey(date)
    if (mayBeDateKey) return datePartsToKeys(mayBeDateKey)
  }

  const parsedDate = new Date(date)
  if (isNaN(parsedDate.getTime())) return null

  const dateParts = extractDateParts(parsedDate, timeZone)
  if (!dateParts) return null

  return datePartsToKeys(dateParts)
}

export const extractDateParts = (date: Date, timeZone: string): DateParts | null => {
  if (isNaN(date.getTime())) return null

  const formatter = new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone,
    year: "numeric",
  })

  const parts = formatter.formatToParts(date)
  const year = parts.find((part) => part.type === "year")?.value
  const month = parts.find((part) => part.type === "month")?.value
  const day = parts.find((part) => part.type === "day")?.value

  if (!year || !month || !day) return null
  return { year, month, day }
}

export const extractDatePartsFromDateKey = (dateKey: string): DateParts | null => {
  const match = dateKey.match(DATE_KEY_REGEX)
  if (!match) return null
  const [, year, month, day] = match
  return { year, month, day }
}

export const extractDayOfWeek = (date: Date | string, timeZone: string): DayOfWeek | null => {
  const d = typeof date === "string" ? new Date(date) : date

  if (isNaN(d.getTime())) return null

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
  })

  const weekday = formatter.format(d)
  return DAY_OF_WEEK_MAP[weekday] ?? null
}

const datePartsToKeys = ({ year, month, day }: DateParts): DateKeys => {
  return {
    dateKey: `${year}-${month}-${day}`,
    monthDayKey: `${month}-${day}`,
  }
}
